import sanitizeHtml from 'sanitize-html';
export default function (){
    // 様々なコンポーネントで使えそうな処理はこちらで定義
    const getSanitizeHtml = (content) => {
        return sanitizeHtml(content, {
            allowedTags: ['p', 'strong', 'a', 'span'], // 許可するタグ
            allowedAttributes: {
                a: ['href', 'rel', 'target'],
                span: ['style', 'class'],
                div: ['style', 'class'],
            }, // 許可する属性
            allowedIframeHostnames: [] // 許可するiframeのホスト名
        })
    }
    // プロジェクトのロゴ画像のパスを取得
    const getPjLogoImagePath = (code) => {
        return '/static/web/img/' + code + '/' + code + '.png';
    };

    const MAIL_BODY_CONFIG = {
        default: {
            loopGroups: [],
            valueMap: {},
            blankLineBeforeKeys: [],
        },
        shironekoproject: {
            // ループ項目をペアで束ねたい組み合わせを宣言
            // 配列内の並びがペアの順序になる: 質問1, 答え1, 質問2, 答え2 ...
            // 戻る操作などで後から追加された項目も、最初の項目の挿入位置にまとめて配置される
            loopGroups: [
                ['登録された秘密の質問', '秘密の質問の答え'],
            ],
            valueMap: {
                '外部アカウント認証によるデータ復旧をお試しください。': {
                    '解決しない場合はこちら': '解決しない',
                },
                '登録メールアドレスからのパスワード再発行をお試しください。': {
                    '解決しない場合はこちら': '解決しない',
                },
                'どの方法で引き継ぎ用のアカウント登録を行われましたか。': {
                    '登録していない\n覚えていない': '登録していない・覚えていない',
                },
            },
            blankLineBeforeKeys: [
                'どの方法で引き継ぎ用のアカウント登録を行われましたか。',
                '購入内容',
                '復旧希望のアカウントでログインしたことがある端末は所有していますか。',
            ],
        },
    };

    const getMailBodyConfig = (code) => {
        return MAIL_BODY_CONFIG[code] ?? MAIL_BODY_CONFIG.default;
    };

    // 初回 set 順に並び替えた新しい inputValues オブジェクトを返す
    // keyFirstSetOrder に登録がないキーは Infinity 扱いで末尾へ（安定ソート）
    const sortByFirstSetOrder = (inputValues, keyFirstSetOrder) => {
        const sortedKeys = Object.keys(inputValues).sort((a, b) => {
            const oa = keyFirstSetOrder[a] ?? Infinity;
            const ob = keyFirstSetOrder[b] ?? Infinity;
            return oa - ob;
        });
        const result = {};
        for (const key of sortedKeys) {
            result[key] = inputValues[key];
        }
        return result;
    };

    // 挿入順を尊重しつつ、loopGroups で指定したループ項目だけは
    // 最初の出現位置にまとめて (サフィックスでペア展開して) 配置する
    const sortKeysWithLoopGroups = (inputValues, loopGroups) => {
        const allKeys = Object.keys(inputValues);

        // 各キーがどのグループ・どの基底・どのサフィックスに属するかを判定
        const findGroupInfo = (key) => {
            for (let gi = 0; gi < loopGroups.length; gi++) {
                const bases = loopGroups[gi];
                for (let bi = 0; bi < bases.length; bi++) {
                    const base = bases[bi];
                    if (key === base) {
                        return { groupIndex: gi, baseIndex: bi, suffix: 0 };
                    }
                    if (key.startsWith(base)) {
                        const suffixStr = key.slice(base.length);
                        if (/^\d+$/.test(suffixStr)) {
                            return { groupIndex: gi, baseIndex: bi, suffix: parseInt(suffixStr, 10) };
                        }
                    }
                }
            }
            return null;
        };

        // キー → グループ情報 のマップを構築
        const keyToGroupInfo = {};
        const groupMembers = loopGroups.map(() => []);
        for (const key of allKeys) {
            const info = findGroupInfo(key);
            if (info !== null) {
                keyToGroupInfo[key] = info;
                groupMembers[info.groupIndex].push({ key, ...info });
            }
        }

        // 各グループ内をソート: サフィックス昇順 → 同サフィックス内は bases の並び順
        for (const members of groupMembers) {
            members.sort((a, b) => {
                if (a.suffix !== b.suffix) return a.suffix - b.suffix;
                return a.baseIndex - b.baseIndex;
            });
        }

        // 挿入順に出力。グループ所属キーに初めて出会った時点でグループ全体を展開し、以降はスキップ
        const emittedGroups = new Set();
        const result = [];
        for (const key of allKeys) {
            const info = keyToGroupInfo[key];
            if (info) {
                if (!emittedGroups.has(info.groupIndex)) {
                    emittedGroups.add(info.groupIndex);
                    for (const member of groupMembers[info.groupIndex]) {
                        result.push(member.key);
                    }
                }
                // すでに展開済みのグループメンバーはスキップ
            } else {
                result.push(key);
            }
        }
        return result;
    };

    const getMailBody = (isIncludeUserAgent, inputValues, code = '', keyFirstSetOrder = {}) => {
        const config = getMailBodyConfig(code);
        const valueMap = config.valueMap ?? {};
        const blankLineBeforeKeySet = new Set(config.blankLineBeforeKeys ?? []);

        const sortedInputValues = sortByFirstSetOrder(inputValues, keyFirstSetOrder);
        const orderedKeys = sortKeysWithLoopGroups(sortedInputValues, config.loopGroups ?? []);
        const lines = [];

        for (let i = 0; i < orderedKeys.length; i++) {
            const key = orderedKeys[i];
            const rawValue = inputValues[key];
            const mappedValue = valueMap[key]?.[rawValue] ?? rawValue ?? '';

            if (i > 0 && blankLineBeforeKeySet.has(key) && lines[lines.length - 1] !== '') {
                lines.push('');
            }

            lines.push('・' + key + ': ' + mappedValue);
        }

        let text = lines.join('\n');
        if (text.length > 0) {
            text += '\n';
        }

        if (isIncludeUserAgent) {
            text += '\n※以下は消さないでください\n' + window.navigator.userAgent.toLowerCase() + '\n';
        }
        return text;
    }

    return {
        getSanitizeHtml,
        getPjLogoImagePath,
        getMailBody: getMailBody,
    }
}